// frontend/src/components/Navbar.js
import React, { useState } from 'react';
import { Link, useNavigate, useLocation, useSwitchLanguage } from '../routing';
import { useAuth } from '../context/AuthContext';
import { Menu, X, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { rememberLocale } from '../utils/locale';
import { LANGUAGES, nextLanguage } from '../i18n';
import theme from '../theme';

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const { t, i18n } = useTranslation();

  const handleLogout = () => { logout(); navigate('/'); };

  // Navigates rather than only swapping the strings: with one URL per language
  // (/el/…, /en/…) leaving the path untouched would put English text under a
  // URL that still said /el — wrong for the reader who shares the link, and a
  // contradiction for the crawler that was told those are two pages.
  const switchLanguage = useSwitchLanguage();

  const toggleLang = () => {
    const next = nextLanguage(i18n.language);
    switchLanguage(next);
    rememberLocale(next);
  };

  const navLinks = [
    { to: '/browse', label: t('nav.browse') },
    { to: '/how-it-works', label: t('nav.howItWorks') },
    ...(user ? [{ to: '/my-rentals', label: t('nav.myRentals') }, { to: '/profile', label: t('nav.profile') }] : []),
    ...(user?.role === 'admin' ? [{ to: '/admin', label: t('nav.admin') }] : []),
  ];

  // The button shows where it will take you, not where you are.
  const langLabel = LANGUAGES[nextLanguage(i18n.language)]?.short ?? 'EN';

  return (
    // safe-top lets the bar's own background extend under the status bar while
    // its contents sit below it. Padding the page instead would leave a sticky
    // bar pinned to top:0 — i.e. underneath the clock — on Android 15.
    <nav className="safe-top safe-x" style={{
      background: theme.alpha.white(0.95),
      backdropFilter: 'blur(20px)',
      borderBottom: `1px solid ${theme.sand}`,
      position: 'sticky', top: 0, zIndex: 100,
      fontFamily: theme.font.display
    }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 64 }}>

        {/* Logo */}
        <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Sparkles size={20} color={theme.gold} />
          <span style={{ fontSize: 24, fontWeight: 600, color: theme.ink, letterSpacing: '0.05em' }}>
            GLAM<span style={{ color: theme.gold }}>GO</span>
          </span>
        </Link>

        {/* Desktop nav */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 32 }} className="desktop-nav">
          {navLinks.map(l => (
            <Link key={l.to} to={l.to} style={{
              textDecoration: 'none',
              color: location.pathname === l.to ? theme.gold : theme.grey600,
              fontSize: 15, letterSpacing: '0.08em',
              transition: 'color 0.2s',
              fontWeight: location.pathname === l.to ? 600 : 400,
              // CSS uppercase, not .toUpperCase(): JS keeps the Greek tonos
              // ('Αναζήτηση' → 'ΑΝΑΖΉΤΗΣΗ'), which is a spelling error in
              // all-caps Greek. Every other surface in the app already does
              // this; the shared navbar was the one that did not.
              textTransform: 'uppercase',
            }}>{l.label}</Link>
          ))}

          {/* Language switcher */}
          <button onClick={toggleLang} aria-label={t('nav.switchLanguage')} style={{
            background: 'transparent', border: `1px solid ${theme.sand}`,
            color: theme.grey400, padding: '6px 14px', borderRadius: 4,
            cursor: 'pointer', fontSize: 12, letterSpacing: '0.12em',
            fontFamily: theme.font.body, fontWeight: 500,
            transition: 'border-color 0.2s, color 0.2s'
          }}
            onMouseEnter={e => { e.target.style.borderColor = theme.gold; e.target.style.color = theme.gold; }}
            onMouseLeave={e => { e.target.style.borderColor = theme.sand; e.target.style.color = theme.grey400; }}
          >{langLabel}</button>

          {user ? (
            <button onClick={handleLogout} style={{
              background: 'transparent', border: `1px solid ${theme.gold}`,
              color: theme.gold, padding: '8px 20px', borderRadius: 4,
              cursor: 'pointer', fontSize: 13, letterSpacing: '0.1em',
              fontFamily: 'inherit', textTransform: 'uppercase',
            }}>{t('nav.logout')}</button>
          ) : (
            <Link to="/login" style={{
              background: theme.ink, color: theme.white,
              padding: '10px 24px', borderRadius: 4, textDecoration: 'none',
              fontSize: 13, letterSpacing: '0.1em', textTransform: 'uppercase',
            }}>{t('nav.signIn')}</Link>
          )}
        </div>

        {/* Mobile hamburger */}
        <button onClick={() => setOpen(!open)} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'none' }} className="mobile-menu-btn">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div style={{ background: theme.white, borderTop: `1px solid ${theme.sand}`, padding: 24 }}>
          {navLinks.map(l => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} style={{
              display: 'block', padding: '12px 0',
              textDecoration: 'none', color: theme.ink,
              fontSize: 16, borderBottom: `1px solid ${theme.creamBone}`,
              letterSpacing: '0.05em'
            }}>{l.label}</Link>
          ))}
          <button onClick={toggleLang} aria-label={t('nav.switchLanguage')} style={{
            marginTop: 16, width: '100%', padding: '12px',
            background: 'transparent', border: `1px solid ${theme.sand}`,
            color: theme.grey400, borderRadius: 4, fontSize: 13, cursor: 'pointer',
            fontFamily: theme.font.body, letterSpacing: '0.1em'
          }}>{langLabel}</button>
          {user ? (
            <button onClick={handleLogout} style={{
              marginTop: 12, width: '100%', padding: '12px',
              background: 'transparent', border: `1px solid ${theme.gold}`,
              color: theme.gold, borderRadius: 4, fontSize: 15, cursor: 'pointer'
            }}>{t('nav.logout')}</button>
          ) : (
            <Link to="/login" onClick={() => setOpen(false)} style={{
              display: 'block', marginTop: 12, textAlign: 'center',
              background: theme.ink, color: theme.white, padding: '14px',
              borderRadius: 4, textDecoration: 'none', fontSize: 15
            }}>{t('nav.signIn')}</Link>
          )}
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .desktop-nav { display: none !important; }
          .mobile-menu-btn { display: block !important; }
        }
      `}</style>
    </nav>
  );
}
